/* eslint-disable camelcase */
import React, { useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { Heading, Content, Figure } from '../ReportComponent/ReportComponent';
import Icon from '../../../../../../components/Icon/Icon';
import RiskGradeStatusBar from '../../../../../NatCatProfile/components/RiskGradeStatusBar/RiskGradeStatusBar';
import NatCatMap from '../../../../../NatCatProfile/components/NatCatMap/NatCatMap';
import natcatData from '../../../../../NatCatProfile/natcatData';
import NatCatChart from '../../../../../NatCatProfile/components/NatCatChart/NatCatChart';
import { sectionsSelector } from '../../../../ReportSelectors';
import { appThemeSelector } from '../../../../../../components/App/AppSelectors';
import themes from '../../../../../../shared/style/themes/themes';
import { getNatCatRiskGradeColour, renderGradeColour } from '../../../../../NatCatProfile/utils';

import './NatCatDescription.scss';
import { setNatCatIndexChartDataUrl } from '../../../../ReportActions';

const Results = ({ legends = [], analysis = [], showMap, classifications, loadingMap }) => (
  <>
    <div className="results-container">
      <h3>
        Result : <span>{analysis?.hazard || analysis?.return_period}</span>
      </h3>
      <div className="legends-subtitle">{legends?.subtitle}</div>
      <RiskGradeStatusBar
        maxLength={legends?.rules?.length || 0}
        length={analysis?.risk_index || 0}
        colour={getNatCatRiskGradeColour(analysis?.hazard || analysis?.return_period, legends)}
      />
      {showMap && (
        <div className="map">
          <NatCatMap layerId={legends?.layerId} loading={loadingMap} />
        </div>
      )}
    </div>
    <div className="legends">
      <h5>Risk Grades:</h5>
      <div className="legend-tiles">
        {legends.rules
          .filter((r) => r.title !== 'No Data')
          .map((rule) => (
            <div className="legend-tile" key={rule.title}>
              <div
                className="legend-fill"
                style={{
                  backgroundColor: rule.symbolizers?.[0]?.raster?.color || rule.symbolizers?.[0]?.polygon?.fill,
                }}
              />
              <div className="legend-title">{rule.title}</div>
            </div>
          ))}
        <div className="risk-classification">
          <div>
            Low risks: <br />
            {classifications?.risk_categories?.low_risks.map((risk, i) => (
              <span key={risk.item} className={classNames('risk-item', { first: i === 0 })}>
                {risk.item}
              </span>
            ))}{' '}
          </div>
          <div>
            Medium risks: <br />
            {classifications?.risk_categories?.medium_risks.map((risk, i) => (
              <span key={risk.item} className={classNames('risk-item', { first: i === 0 })}>
                {risk.item}
              </span>
            ))}
          </div>
          <div>
            High risks: <br />
            {classifications?.risk_categories?.high_risks.map((risk, i) => (
              <span key={risk.item} className={classNames('risk-item', { first: i === 0 })}>
                {risk.item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </>
);

const NatCatDescription = ({
  sections,
  appTheme,
  figureSeq,
  natcatProfiles,
  loadingNatCatMap,
  natcatIndexChartDataUrl,
  setNatCatIndexChartDataUrl,
}) => {
  const filteredNatcatProfiles = useMemo(() => {
    return natcatProfiles?.filter(({ id }) => sections?.['report-natural-catastrophe']?.children?.includes(id)) || [];
  }, [natcatProfiles, sections]);

  useEffect(() => {
    if (natcatProfiles) {
      setNatCatIndexChartDataUrl(null);
    }
  }, [natcatProfiles, setNatCatIndexChartDataUrl]);

  const onChartReady = (chart) => {
    setNatCatIndexChartDataUrl(chart.getDataURL());
  };

  // Only render if the conditions are met
  if (
    !(
      sections &&
      sections['report-natural-catastrophe'] &&
      sections['report-natural-catastrophe'].checked &&
      appTheme === themes.iprofile
    )
  ) {
    return null;
  }

  return (
    <div className="report-preview-no-break">
      <Heading seq={sections['report-natural-catastrophe'].seq} anchor="report-natural-catastrophe">
        Our Nat Cat source
      </Heading>
      <Content printGap="medium">
        <p>
          iProfileRisk NatCat data is directly sourced from SwissRe. Using global loss data gathered over decades by one
          of the worlds largest and most trusted re insurers ‘SwissRe’ CatNet provides detailed risk data for a given
          location using historical records. The purpose of which is to provide you with a guide to assess the natural
          risks facing the location in question. CatNet allows you to access, analyse and benchmark using risk data
          through the most recent natural hazard exposures and carry out analysis on near real time event footprints for
          earthquakes, tropical cyclones, floods, bushfires, and volcanic eruption to name a few.
        </p>

        {filteredNatcatProfiles.length > 0 && (
          <div className="natcat-chart-container">
            <Figure seq={figureSeq} caption="Natural Catastrophe Index">
              {natcatIndexChartDataUrl ? (
                <img width={680} src={natcatIndexChartDataUrl} alt="natcat-index" />
              ) : (
                <NatCatChart
                  data={filteredNatcatProfiles.filter((p) => p.analysis.graph_value > 0).reverse()}
                  direction="horizontal"
                  print
                  onChartReady={onChartReady}
                />
              )}
            </Figure>
          </div>
        )}
        {natcatProfiles &&
          natcatProfiles.map(
            ({ id, name, map, analysis, risk_classifications, legends, sources }) =>
              sections['report-natural-catastrophe']?.children?.includes(id) && (
                <table
                  id={id}
                  key={id}
                  className={classNames('report-preview-table', 'report-natural-catastrophe-table')}
                >
                  <tbody>
                    <tr className="profile-name">
                      <td>
                        <h2>
                          <span className="title">
                            <Icon
                              component={natcatData?.[id]?.icon}
                              size={36}
                              className="report-natcat-icon"
                              style={{
                                backgroundColor: renderGradeColour(analysis?.risk_description),
                                fontSize: 24,
                              }}
                            />{' '}
                            {name}
                          </span>
                          <span
                            className="risk-grade"
                            style={{
                              color:
                                renderGradeColour(analysis?.risk_description) ||
                                getNatCatRiskGradeColour(analysis?.risk_description, legends),
                            }}
                          >
                            {analysis?.risk_description || 'No risk data'}
                          </span>
                        </h2>
                      </td>
                    </tr>
                    <tr className="definition">
                      <td>
                        <h3>Definition</h3>
                        {natcatData?.[id]?.definition}
                      </td>
                    </tr>
                    <tr>
                      <td className="results">
                        <Results
                          layerName={name}
                          legends={legends}
                          analysis={analysis}
                          sources={sources}
                          classifications={risk_classifications}
                          map={map}
                          showMap={
                            sections['report-natural-catastrophe']?.children &&
                            sections['report-natural-catastrophe'].children.includes(id)
                          }
                          loadingMap={loadingNatCatMap.includes(legends?.layerId)}
                        />
                      </td>
                    </tr>
                    <tr className="sources">
                      <td>
                        <h3>Sources : </h3>
                        {natcatData?.[id] && natcatData[id].sources}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )
          )}
      </Content>
    </div>
  );
};

const mapStateToProps = (state) => ({
  sections: sectionsSelector(state),
  appTheme: appThemeSelector(state),
  natcatProfiles: state.natcat.natcatProfiles,
  natcatIndexChartDataUrl: state.report.natcatIndexChartDataUrl,
  loadingNatCatProfiles: state.natcat.loadingNatCatProfiles,
  loadingNatCatMap: state.natcat.loadingNatCatMap,
});

const mapDispatchToProps = { setNatCatIndexChartDataUrl };

export default connect(mapStateToProps, mapDispatchToProps)(NatCatDescription);
